import { FC, useState } from "react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Modal } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { deleteOffice } from "@/api/offices";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import { VariantType } from "notistack";
import { Office } from "../offices.types";
import { fetchOfficesList, useOfficesListQuery } from "../offices.query";
import {
  LAST_OFFICE_MESSAGE,
  OFFICE_COUNT_UNAVAILABLE_MESSAGE,
} from "../offices.constants";

interface DeleteOfficeModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  office: Office;
  buttonProps?: {
    submitText?: string;
    cancelText?: string;
  };
}

export const DeleteOfficeModal: FC<DeleteOfficeModalProps> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  office,
  buttonProps = {},
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Deleting the last office orphans every user and storage location assigned
  // to it, so the count gates both the confirm button and handleSubmit below.
  // The API layer resolves with { success: false } instead of rejecting, so a
  // failed fetch still reports isSuccess and leaves a non-array in data —
  // Array.isArray is what separates "loaded" from "could not read".
  const { data: offices, isPending: officesPending } = useOfficesListQuery();
  const officesLoaded = Array.isArray(offices);
  const isLastOffice = officesLoaded && offices.length <= 1;

  // Replaces the confirmation copy when the delete is blocked. Stays null
  // while the list is still in flight — that state disables the confirm but
  // has no reason to give yet.
  const blockingMessage = isLastOffice
    ? LAST_OFFICE_MESSAGE
    : !officesLoaded && !officesPending
    ? OFFICE_COUNT_UNAVAILABLE_MESSAGE
    : null;

  const handleSubmit = async () => {
    setSubmitting(true);

    // The global axios response interceptor in src/api/connection.js already
    // toasts the server's message for every non-401 failure, so a request
    // error must not enqueue a second toast from here.
    let alreadyToasted = false;

    try {
      // The cached count can be minutes old — another user may have deleted an
      // office since this page loaded, and there is no backend guard until
      // ENG-1801 lands. Re-read the list before committing to the delete.
      const latestOffices = await fetchOfficesList(queryClient).catch(
        (error) => {
          console.error(error);
          return undefined;
        }
      );

      // Both of these also disable the confirm button, so reaching them means
      // either the disabled state was bypassed or the count changed under us.
      // Bail out before the request so no office:delete log is written and we
      // don't navigate away.
      if (!Array.isArray(latestOffices)) {
        enqueueSnackbar(OFFICE_COUNT_UNAVAILABLE_MESSAGE, {
          variant: "warning" as VariantType,
        });
        return;
      }

      if (latestOffices.length <= 1) {
        enqueueSnackbar(LAST_OFFICE_MESSAGE, {
          variant: "warning" as VariantType,
        });
        return;
      }

      const result = await deleteOffice(office.office_id);

      // The API layer resolves (never rejects) on HTTP errors — treat a
      // { success: false } result as failure.
      if (result?.success === false) {
        alreadyToasted = true;
        throw new Error("Failed to delete office");
      }

      onSubmit?.();

      enqueueSnackbar(`Office "${office.office_name}" deleted`, {
        variant: "success" as VariantType,
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Deleted Office: ${office.office_name}`,
        event: "office:delete",
        object_type: "office",
      });

      navigate("/offices");
      onClose();
    } catch (error) {
      console.error(error);
      if (!alreadyToasted) {
        enqueueSnackbar(
          `An error occurred while deleting office "${office.office_name}". Please try again.`,
          { variant: "error" as VariantType }
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  return (
    <Modal open={show} onClose={onClose}>
      <Modal.Title>Delete Office</Modal.Title>
      <div style={{ fontWeight: 500, margin: "20px 0px", width: "400px" }}>
        {blockingMessage ?? (
          <>
            Are you sure you want to delete office{" "}
            <span style={{ fontWeight: 800 }}>"{office.office_name}"</span>?
            <p>This action cannot be undone.</p>
          </>
        )}
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || isLastOffice || !officesLoaded}
          loading={submitting}
        >
          {buttonProps?.submitText || "Delete Office"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
